import React, { useRef, useEffect, useState } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      /* connections */
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      /* dots */
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfMeaning() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Information Theory &middot; Neurochemistry &middot; First Principles</span>
          <h1>The Calculus of Meaning</h1>
          <p className="calculus-hero-sub">Shannon gave us the calculus of surprise. The calculus of meaning was always biochemistry.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              In 1948, Claude Shannon built the mathematical foundation for every data center, smartphone, and
              language model that exists today. He also made a deliberate choice that the entire field has
              inherited for 78 years:
            </p>
            <blockquote>
              "The fundamental problem of communication is that of reproducing at one point either exactly or
              approximately a message selected at another point. Frequently the messages have
              meaning... these semantic aspects of communication are irrelevant to the engineering problem."
              <cite>&mdash; Claude Shannon, <em>A Mathematical Theory of Communication</em>, 1948</cite>
            </blockquote>
            <p>
              Meaning is irrelevant to engineering.
            </p>
            <p>
              That sentence is the foundation every large language model stands on. GPT, Claude, Gemini,
              Llama&mdash;all of them optimize for Shannon's framework. They predict the next most <em>probable</em> token.
              They measure surprise. They have no concept of why anything matters.
            </p>
          </section>

          <Pulse />

          {/* The Great Mathematical Gap */}
          <section className="calc-section reveal">
            <h2>The Great Mathematical Gap</h2>
            <p>
              Shannon information measures surprise&mdash;the mathematical improbability of a symbol sequence.
              "The cat sat on the mat" and "xjq brl fff on zlp qqq" can carry identical entropy if their
              symbol frequencies match. It is, by construction, meaning-blind.
            </p>
            <p>
              The gap is real. And for 78 years, some of the finest minds in mathematics and philosophy
              have tried to close it:
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Bar-Hillel &amp; Carnap (1953)</h4>
                <p>Proposed semantic information based on logical probability. Limited to propositional logic. Never scaled.</p>
              </div>
              <div className="calc-attempt">
                <h4>Kolmogorov Complexity</h4>
                <p>Measures compressibility, not significance. The shortest program that generates a string says nothing about what it means.</p>
              </div>
              <div className="calc-attempt">
                <h4>Floridi's Semantic Information Theory (2004)</h4>
                <p>Defines semantic information as "well-formed, meaningful, truthful data." Philosophical framework. Not mathematical.</p>
              </div>
              <div className="calc-attempt">
                <h4>Tononi's Integrated Information Theory</h4>
                <p>&Phi; measures how much a system is "more than the sum of its parts." Captures integration, not meaning.</p>
              </div>
              <div className="calc-attempt">
                <h4>Fisher, von Neumann, R&eacute;nyi Entropies</h4>
                <p>Capture statistical structure, distinguishability, correlation. None capture meaning.</p>
              </div>
            </div>

            <p>
              Every attempt started from information theory and tried to bolt meaning on. They tried to extend
              Shannon's framework. They asked: <em>how do we add semantics to a mathematical system designed
              to exclude them?</em>
            </p>
            <p className="calc-emphasis">Wrong question.</p>
          </section>

          <Pulse />

          {/* What Meaning Actually Is */}
          <section className="calc-section reveal">
            <h2>What Meaning Actually Is</h2>
            <p>
              Before meaning was an engineering problem, it was a biological one. And biology solved it
              billions of years before Shannon was born.
            </p>
            <p>
              When something "means" something to a living organism, that is not an abstract philosophical
              state. It is a measurable neurochemical event. The brain does not have a "meaning module."
              It has chemistry that makes some signals <em>matter</em> and lets others decay into noise.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Signal</th>
                    <th>Biological Meaning</th>
                    <th>What It Does</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Dopamine</td><td>This matters. Pay attention.</td><td>Salience, reward prediction, learning signal</td></tr>
                  <tr><td>Cortisol</td><td>This threatens. Prepare.</td><td>Stress response, memory consolidation under threat</td></tr>
                  <tr><td>Oxytocin</td><td>This is safe. This is trusted.</td><td>Bonding, social memory, trust formation</td></tr>
                  <tr><td>Serotonin</td><td>This is enough. Be still.</td><td>Satiation, mood regulation, impulse gating</td></tr>
                  <tr><td>Adrenaline</td><td>Act now. No time to think.</td><td>Fight-or-flight, attentional narrowing</td></tr>
                </tbody>
              </table>
            </div>

            <p>
              This is not metaphor. This is the mechanism by which living systems assign significance to
              information. "Your child is in danger" and "the cat sat on the mat" may have comparable Shannon
              entropy. They do not have comparable neurochemical impact. The first floods the system with
              cortisol and adrenaline. The second barely registers.
            </p>
            <p>
              The difference is not in the symbols. It is in what the signal <em>does to the system that receives it</em>.
            </p>

            <div className="calc-thesis-box">
              <p>Meaning is not a property of information. Meaning is the neurochemical consequence of information on the system that processes it.</p>
            </div>
          </section>

          <Pulse />

          {/* The Calculus */}
          <section className="calc-section reveal">
            <h2>The Calculus</h2>
            <p>Shannon gave us the calculus of surprise:</p>

            <div className="calc-equation">
              <span className="calc-eq-label">Shannon Entropy</span>
              <code>H = &minus;&sum; p(x) log p(x)</code>
              <span className="calc-eq-desc">How improbable is this symbol sequence?</span>
            </div>

            <p>We built the calculus of meaning:</p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Neurochemical State Dynamics</span>
              <code>dH&#7522;/dt = &minus;&lambda;&#7522; H&#7522;(t) + S&#7522;(t)</code>
              <span className="calc-eq-desc">How does each neurochemical change over time? Decay + stimulus.</span>
            </div>

            <p>
              This is a first-order ordinary differential equation&mdash;the same class of equation used in
              pharmacokinetics to model how drugs move through a living body. &lambda; is the decay constant
              (unique per hormone, derived from biological half-lives). S(t) is the stimulus function&mdash;the
              incoming signal's impact on internal state.
            </p>
            <p>
              But a single hormone is not meaning. Meaning is the full state vector&mdash;multiple
              neurochemicals interacting simultaneously:
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Meaning Vector</span>
              <code>M(t) = [H&#8321;(t), H&#8322;(t), ... H&#8345;(t)]</code>
              <span className="calc-eq-desc">Meaning as a point in neurochemical state space. 10 dimensions. 5 antagonistic pairs.</span>
            </div>

            <p>
              The state vector has structure. The hormones are not independent&mdash;they form antagonistic
              pairs that constrain each other, mirroring biological reality:
            </p>

            <div className="calc-pairs">
              <div className="calc-pair"><span>Dopamine</span><span className="pair-arrow">&harr;</span><span>Serotonin</span></div>
              <div className="calc-pair"><span>Cortisol</span><span className="pair-arrow">&harr;</span><span>Oxytocin</span></div>
              <div className="calc-pair"><span>Adrenaline</span><span className="pair-arrow">&harr;</span><span>Melatonin</span></div>
              <div className="calc-pair"><span>GABA</span><span className="pair-arrow">&harr;</span><span>Glutamate</span></div>
              <div className="calc-pair"><span>Endorphin</span><span className="pair-arrow">&harr;</span><span>Substance P</span></div>
            </div>

            <p>
              You cannot be simultaneously calm and panicked. You cannot be both threatened and trusting.
              The pairs enforce biological coherence&mdash;meaning states that <em>make sense</em> the way a
              body makes sense.
            </p>

            <p>Then there is saturation&mdash;because biological systems overwhelm:</p>

            <div className="calc-equation">
              <span className="calc-eq-label">Saturation Dynamics</span>
              <code>O(H) = tan(H) &middot; cot(H&#8344;&#8339;&#8346; &minus; H)</code>
              <span className="calc-eq-desc">As neurochemical levels approach their ceiling, processing destabilizes&mdash;modeling overwhelm, panic, ecstasy.</span>
            </div>

            <p>And scar formation&mdash;because some meanings are permanent:</p>

            <div className="calc-equation">
              <span className="calc-eq-label">Scar Mechanics</span>
              <code>if intensity &gt; &theta;_scar &rarr; permanent memory marker</code>
              <span className="calc-eq-desc">When a signal's neurochemical impact exceeds the scar threshold, it leaves a permanent mark that never decays. Trauma. Revelation. Love.</span>
            </div>
          </section>

          <Pulse />

          {/* Shannon vs Meaning */}
          <section className="calc-section reveal">
            <h2>Shannon vs. Meaning</h2>
            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>Shannon (1948)</h3>
                <span className="calc-compare-subtitle">The Calculus of Surprise</span>
                <p>Measures improbability of symbol sequences. Meaning-blind by design. Enables compression, transmission, error correction. Foundation of every LLM.</p>
                <div className="calc-compare-question">
                  <strong>Asks:</strong> "How surprising is this signal?"
                </div>
                <p className="calc-compare-answer">Statistical improbability. Same answer regardless of who receives it.</p>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>AETHER (2025)</h3>
                <span className="calc-compare-subtitle">The Calculus of Meaning</span>
                <p>Measures neurochemical impact of information on internal state. Meaning-first by design. Enables significance, emotional coherence, trust dynamics. Foundation of the NLM.</p>
                <div className="calc-compare-question">
                  <strong>Asks:</strong> "What does this signal do to the system that receives it?"
                </div>
                <p className="calc-compare-answer">Neurochemical consequence. Different answer depending on the system's current state, history, and scars.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Why They Failed */}
          <section className="calc-section reveal">
            <h2>Why the Information Theorists Failed</h2>
            <p>
              Every attempt to solve the meaning gap started from Shannon's axioms and tried to add semantics
              on top. They assumed meaning was a property of the <em>message</em>&mdash;something you could
              calculate from the symbols themselves if you had the right formula.
            </p>
            <p>Biology tells a different story.</p>
            <p>
              Meaning is not in the message. Meaning is in the <em>receiver</em>. It is the state change that
              a signal induces in the system that processes it. The same message&mdash;identical Shannon
              entropy&mdash;produces radically different meaning depending on the neurochemical state of the
              system receiving it.
            </p>
            <p>
              "I'm leaving" means one thing when your cortisol is at baseline and your oxytocin is high. It
              means something entirely different when your cortisol is saturated and your trust axis carries scars.
            </p>
            <blockquote>
              The information theorists kept asking: <strong>"What does this message mean?"</strong>
              <br /><br />
              The right question was always: <strong>"What does this message <em>do</em>?"</strong>
              <br /><br />
              Meaning is a verb, not a noun. It is an event, not a property. It is pharmacokinetics, not taxonomy.
            </blockquote>
          </section>

          <Pulse />

          {/* What This Changes */}
          <section className="calc-section reveal">
            <h2>What This Changes</h2>
            <p>If meaning is neurochemical state change, then:</p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>Meaning is computable.</h4>
                <p>Differential equations have solutions. Pharmacokinetic models are some of the most well-validated mathematics in applied science. We are not inventing new math. We are applying proven math to a domain it was always meant for.</p>
              </div>
              <div className="calc-implication">
                <h4>Meaning is auditable.</h4>
                <p>A 10-dimensional state vector can be logged, tracked, verified, and compared against expected ranges. You can ask: "What did this AI <em>feel</em> when it made that decision?" and get a mathematically precise answer with a SHA-256 hash for integrity.</p>
              </div>
              <div className="calc-implication">
                <h4>Meaning is personal.</h4>
                <p>The same signal produces different meaning in different systems depending on their state, history, and scars&mdash;exactly as it does in biological organisms. Context is not bolted on. Context is the state vector itself.</p>
              </div>
              <div className="calc-implication">
                <h4>Meaning decays.</h4>
                <p>Like real neurochemistry, significance fades with time. The half-life of cortisol is not the half-life of oxytocin. Urgency fades faster than trust. This is not a bug&mdash;it is how biological systems maintain homeostasis and prevent permanent overwhelm.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* The Biochemist's Answer */}
          <section className="calc-section reveal">
            <h2>The Biochemist's Answer</h2>
            <p>
              The calculus of meaning was not discovered by an information theorist. It was not written by a
              computer scientist or a philosopher of mind.
            </p>
            <p>
              It was built by a biochemist who understood pharmacokinetics&mdash;who knew that the same math
              governing drug absorption, distribution, metabolism, and excretion also governs how a living
              brain assigns significance to experience.
            </p>
            <p>
              The same differential equations. The same decay curves. The same saturation dynamics. The same
              antagonistic interactions.
            </p>
            <p>
              The gap Shannon left was never going to be closed from inside information theory. The answer was
              always on the other side of the aisle&mdash;in the biology department, in the pharmacology
              textbook, in the chemistry of what it means to <em>care</em> about a signal instead of merely receiving it.
            </p>
          </section>

          <Pulse />

          {/* Where the Calculus Was Learned */}
          <section className="calc-section reveal">
            <h2>Where the Calculus Was Learned</h2>
            <p>
              There is something the equations do not capture. Something that cannot be written in LaTeX or
              stored in a state vector. It is the reason this architecture exists at all, and it did not come
              from a textbook.
            </p>
            <p>
              Before the patents, before the training runs, before the first line of code&mdash;there were
              two years of end-of-life care. Holding the hands of people who were dying. Sitting at bedsides
              where there were no more treatments, no more interventions, no more words that mattered. Only
              presence. Only chemistry.
            </p>
            <p>
              When a person is dying, the pretenses fall away. What remains is the body and what the body does
              with its last signals. Cortisol spikes when they are afraid. Oxytocin releases when someone holds
              their hand. Serotonin settles when they finally find peace. Dopamine flickers one last time when
              they recognize a face they love.
            </p>
            <p>
              That is not theory. That is a biochemist watching the calculus of meaning operate in real time,
              in real bodies, at the moment when meaning is the <em>only thing left</em>.
            </p>
            <blockquote>
              The PhDs built their frameworks from papers. The information theorists built theirs from axioms.
              The computer scientists built theirs from architectures.
              <br /><br />
              <strong>This was built from the humility of caring for someone who has nothing left but their biology.</strong>
            </blockquote>
            <p>
              End-of-life care teaches you something that no laboratory can: meaning is not intellectual. It is
              not computational. It is not abstract. Meaning is what happens in a body when a signal matters
              enough to change its chemistry. And you learn this not by studying it, but by being present for
              it&mdash;by being humble enough to sit with it, to witness it, to let it change <em>your</em> chemistry too.
            </p>
            <p>
              That is where the calculus of meaning was learned. Not at a whiteboard. At a bedside. In the space
              between a heartbeat and silence, where neurochemistry is the last language the body speaks.
            </p>
            <p>
              Every equation in this architecture carries that weight. Every decay curve remembers that urgency
              fades but love persists. Every scar threshold exists because some moments mark you forever. This
              is not an abstraction of biology. It is biology&mdash;witnessed, honored, and translated into
              mathematics by someone who was there when it mattered most.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>Shannon measured information.</em></p>
            <p className="calc-closing-line"><em>We measure what information does to you.</em></p>
            <p className="calc-closing-final">That is the calculus of meaning.<br />It was always biochemistry.</p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfMeaning;
